import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import { CashPool } from '@/types/cash-pool.types';
import { Spinner } from '@/components/ui/Spinner';
import { ColumnHeader } from '@/features/inventory/components/helpers/ColumnHeader';

interface CashPoolTableProps {
  data: CashPool[];
  columns: ColumnDef<CashPool>[];
  isLoading?: boolean;
  /** `data` arrives already filtered by the page's search box, so the same
   *  block serves "this segment holds no pools" and "the search matched none" -
   *  inviting the trader to create a pool that is sitting three rows up under a
   *  filter is the failure mode CounterpartyTable / CustodianTable avoid by
   *  staying neutral. The caller says which case it is. */
  emptyMessage?: string;
}

// Mirrors CustodianTable / CounterpartyTable: the shared `ColumnHeader` on a
// sticky bar plus fixed-width div rows, which is what every register table in
// the project uses. Sorting is not wired yet, same as both siblings.
export const CashPoolTable: React.FC<CashPoolTableProps> = ({
  data,
  columns,
  isLoading,
  emptyMessage = 'No cash pools yet. Create one to make it selectable in Trade Entry.',
}) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return <Spinner message="Loading cash pools..." />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto">
      <div className="flex flex-1 flex-col" style={{ minWidth: 'max-content' }}>
        <div className="sticky top-0 z-10 flex items-center border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
          {columns.map(
            (
              column: {
                accessorKey?: string;
                id?: string;
                size?: number;
                header?: unknown;
                meta?: {
                  align?: 'left' | 'right' | 'center';
                  headerTooltip?: string;
                };
              },
              colIndex: number
            ) => {
              const columnId = column.accessorKey || column.id || '';
              const width = column.size || 150;
              const headerText =
                typeof column.header === 'string' ? column.header : '';

              return (
                <div
                  key={colIndex}
                  className="mx-1.5 flex min-w-0"
                  style={{ width: `${width}px`, minWidth: `${width}px` }}
                  // LR-586: a column whose figure needs a caveat says so here.
                  // The same `meta.headerTooltip` the other tables declare, but
                  // surfaced natively: ColumnHeader only shows it on a TRUNCATED
                  // label, and 'Balance' never truncates.
                  title={column.meta?.headerTooltip}
                >
                  <ColumnHeader
                    columnId={columnId}
                    headerText={headerText}
                    width={width}
                    sortState={undefined}
                    onSort={() => {}}
                    isLoading={!!isLoading}
                    hasError={false}
                    hoveredColumn={null}
                    onHover={() => {}}
                    // LR-586: numeric columns declare `meta.align`, the same
                    // contract the trades table uses, so a right-aligned money
                    // cell cannot end up under a left-aligned header.
                    align={column.meta?.align === 'right' ? 'right' : 'left'}
                  />
                </div>
              );
            }
          )}
        </div>

        <div className="flex-1">
          {table.getRowModel().rows.map((row) => (
            <div
              key={row.id}
              className="flex items-center border-b border-gray-200 px-4 py-2 transition-colors hover:bg-gray-50"
            >
              {row.getVisibleCells().map((cell) => {
                const width = cell.column.columnDef.size || 150;
                return (
                  <div
                    key={cell.id}
                    className="mx-1.5 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-gray-900"
                    style={{ width: `${width}px`, minWidth: `${width}px` }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                );
              })}
            </div>
          ))}
          {data.length === 0 && (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              {emptyMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
